import { useRef } from "react";
import { Loader2, Upload, X, Layers, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * MultiframeRail — a 4-slot ordered keyframe rail shown in the Attach popover
 * when the Video submode is "Multiframes". Each slot is a numbered drop-zone
 * that accepts a single image (uploaded to object storage) and displays a
 * thumbnail. Arrows between slots hint at sequence direction.
 *
 * State is owned by the parent: `slots` is an array of length 4 where each
 * entry is `null` or `{ url, uploaded_url, file }`. Parent forwards
 * `setSlots` and this component drives uploads.
 */

const SLOT_COUNT = 4;

export function MultiframeRail({ slots = [], setSlots, style = null }) {
  // Ensure fixed length
  const rail = Array.from({ length: SLOT_COUNT }).map((_, i) => slots[i] || null);
  const filled = rail.filter(Boolean).length;

  const setSlotAt = (i, next) => {
    setSlots((prev) => {
      const arr = Array.from({ length: SLOT_COUNT }).map((_, j) => prev[j] || null);
      arr[i] = next;
      return arr;
    });
  };

  const upload = async (i, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only images supported for keyframes."); return; }
    if (file.size > 15 * 1024 * 1024)   { toast.error("Max 15MB per keyframe.");                 return; }
    const localUrl = URL.createObjectURL(file);
    setSlotAt(i, { url: localUrl, uploaded_url: null, file, uploading: true });
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (style) fd.append("style", style);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const uploaded_url = data.image_url;
      setSlotAt(i, { url: localUrl, uploaded_url, file, uploading: false });
      toast.success(`Keyframe ${i + 1} locked`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      setSlotAt(i, null);
    }
  };

  return (
    <div className="p-4 rounded-2xl bg-[#0A0A0A]/60 border border-[#00E5FF]/25" data-testid="multiframe-rail">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono-hy text-[10px] text-[#00E5FF] tracking-[0.22em] flex items-center gap-1.5">
          <Layers className="w-3 h-3" /> MULTIFRAME · KEYFRAME ORDER
        </div>
        <div className="font-mono-hy text-[9px] text-white">{filled}/{SLOT_COUNT} filled</div>
      </div>

      <p className="font-mono-hy text-[9px] text-white mb-3 leading-relaxed">
        Drop up to 4 keyframes in the order Seedance should traverse. Slot 1 opens the shot, slot 4 closes it. Empty slots are ignored.
      </p>

      <div className="grid grid-cols-4 gap-2">
        {rail.map((slot, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <MultiframeSlot
              index={i}
              slot={slot}
              onPick={(f) => upload(i, f)}
              onClear={() => setSlotAt(i, null)}
            />
            {i < SLOT_COUNT - 1 && (
              <ArrowRight className="w-3.5 h-3.5 text-[#F2C94C]/40 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiframeSlot({ index, slot, onPick, onClear }) {
  const inputRef = useRef(null);
  const filled = !!slot;
  return (
    <div className="relative flex-1 min-w-0" data-testid={`multiframe-slot-${index}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => onPick(e.target.files?.[0])}
        className="hidden"
        data-testid={`multiframe-slot-${index}-input`}
      />
      {filled ? (
        <div className="relative aspect-square rounded-xl overflow-hidden border border-[#00E5FF]/40 bg-black">
          <img
            src={slot.url}
            alt={`keyframe ${index + 1}`}
            data-testid={`multiframe-slot-${index}-thumb`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {slot.uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
            </div>
          )}
          <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-[#00E5FF] text-[#0A0A0A] text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_-2px_rgba(0,229,255,0.9)]">
            {index + 1}
          </div>
          <button
            onClick={onClear}
            data-testid={`multiframe-slot-${index}-clear`}
            className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0A0A0A]/80 hover:bg-[#EB5757] flex items-center justify-center text-white transition-colors"
            title={`Remove keyframe ${index + 1}`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          data-testid={`multiframe-slot-${index}-pick`}
          className="relative aspect-square w-full rounded-xl border-2 border-dashed border-[#00E5FF]/25 bg-[#00E5FF]/[0.02] hover:border-[#00E5FF] hover:bg-[#00E5FF]/[0.06] hover:shadow-[0_0_18px_-4px_rgba(0,229,255,0.55)] transition-all flex flex-col items-center justify-center gap-1 group"
        >
          <div className="w-5 h-5 rounded-full border border-[#00E5FF]/30 text-[10px] font-mono-hy text-[#00E5FF]/70 flex items-center justify-center group-hover:border-[#00E5FF] group-hover:text-[#00E5FF]">
            {index + 1}
          </div>
          <Upload className="w-4 h-4 text-[#00E5FF]/60 group-hover:text-[#00E5FF]" />
        </button>
      )}
    </div>
  );
}

export default MultiframeRail;
